import { first, isEqual, values } from "lodash";
import { uniqueReferenceNumber } from "utility/helpers/orders";
import { and, assign, createMachine, not, or } from "xstate";
import { CHECKOUT_PAYMENT_TYPE, NEW_ORDER_FORM_STEPS, REPEAT_ORDER_TYPES } from "../../constants";
import {
  calculateOrderPriceActor,
  checkoutActor,
  preCheckoutActor,
  saveBookingActor,
  submitBookingActor,
} from "./newOrder.actors";

const initialContext = {
  order: {},
  customer: {},
  price: [],
  checkout: {
    id: null,
    secret: null,
    error: null,
    expire_at: null,
    cards: [],
  },
  references: [],
  save: false,
  dirty: false,
  loading: false,
};

export default createMachine(
  {
    id: "newOrderForm",
    initial: NEW_ORDER_FORM_STEPS.SET_DELIVERY,
    context: initialContext,
    states: {
      [NEW_ORDER_FORM_STEPS.SET_DELIVERY]: {
        initial: "FORM",
        states: {
          FORM: {
            on: {
              NEXT: [
                { guard: not("isNewOrderChanged"), target: `#newOrderForm.${NEW_ORDER_FORM_STEPS.CALCULATE}` },
                { guard: "isNewOrderChanged", target: "CALCULATING" },
              ],
            },
          },
          CALCULATING: {
            entry: "startLoading",
            invoke: {
              src: "calculateOrderPriceActor",
              input: (input) => ({ ...input }),
              onDone: { target: `#newOrderForm.${NEW_ORDER_FORM_STEPS.CALCULATE}`, actions: "updateOrderAndPrice" },
              onError: { target: "FORM" },
            },
            exit: "endLoading",
          },
        },
      },
      [NEW_ORDER_FORM_STEPS.CALCULATE]: {
        initial: "FORM",
        states: {
          FORM: {
            on: {
              NEXT: [
                // if payment type is by invoice OR is checkout and Stripe was initialized
                {
                  guard: or([not("isImmediatePayment"), "isStripeInitialized"]),
                  target: `#newOrderForm.${NEW_ORDER_FORM_STEPS.BOOKING}`,
                },
                // if payment type is checkout and Stripe wasn't initialized
                { guard: and(["isImmediatePayment", not("isStripeInitialized")]), target: "PRE_CHECKOUT" },
              ],
            },
          },
          PRE_CHECKOUT: {
            entry: "startLoading",
            invoke: {
              src: "preCheckoutActor",
              input: (input) => ({ ...input }),
              onDone: { target: `#newOrderForm.${NEW_ORDER_FORM_STEPS.BOOKING}`, actions: "updateCheckout" },
              onError: { target: "FORM" },
            },
            exit: "endLoading",
          },
        },
      },
      [NEW_ORDER_FORM_STEPS.BOOKING]: {
        initial: "FORM",
        states: {
          FORM: {
            entry: "endLoading",
            on: {
              SUBMIT: [
                { guard: not("isAlreadyCreated"), target: "SUBMIT_BOOKING" },
                { guard: "isAlreadyCreated", target: "CHECKOUT" },
              ],
              CHECKOUT: { target: "CHECKOUT" },
            },
            exit: "startLoading",
          },
          SUBMIT_BOOKING: {
            invoke: {
              src: "submitBookingActor",
              input: (input) => ({ ...input }),
              onDone: [
                // if order shouldn't be saved and payment type is by invoice
                {
                  guard: and([not("shouldSaveBooking"), not("isImmediatePayment")]),
                  target: `#newOrderForm.${NEW_ORDER_FORM_STEPS.FINAL}`,
                  actions: "updateReferences",
                },
                // if order shouldn't be saved and payment type is by invoice
                {
                  guard: and(["shouldSaveBooking", not("isImmediatePayment")]),
                  target: "SAVE_BOOKING",
                  actions: "updateReferences",
                },
                // if payment type is checkout
                { guard: "isImmediatePayment", target: "CHECKOUT", actions: "updateReferences" },
              ],
              onError: { target: "FORM" },
            },
          },
          CHECKOUT: {
            invoke: {
              src: "checkoutActor",
              input: (input) => ({
                ...input,
                event: input.event.output ? { ...input.event, values: input.event.output } : input.event,
              }),
              onDone: [
                // if order shouldn't be saved
                {
                  guard: not("shouldSaveBooking"),
                  target: `#newOrderForm.${NEW_ORDER_FORM_STEPS.FINAL}`,
                  actions: ["updateReferences", "updateCheckoutError"],
                },
                // if order should be saved
                {
                  guard: "shouldSaveBooking",
                  target: "SAVE_BOOKING",
                  actions: ["updateReferences", "updateCheckoutError"],
                },
              ],
              onError: { target: "FORM", actions: "updateCheckoutError" },
            },
          },
          SAVE_BOOKING: {
            invoke: {
              src: "saveBookingActor",
              input: (input) => ({ ...input }),
              onDone: { target: `#newOrderForm.${NEW_ORDER_FORM_STEPS.FINAL}` },
              onError: { target: `#newOrderForm.${NEW_ORDER_FORM_STEPS.FINAL}` },
            },
          },
        },
        exit: "endLoading",
      },
      [NEW_ORDER_FORM_STEPS.FINAL]: {},
    },
    on: {
      RESET: { target: `.${NEW_ORDER_FORM_STEPS.SET_DELIVERY}`, actions: "resetForm" },
      TOGGLE_DIRTY: { actions: "toggleFormDirty" },
      TOGGLE_SHOULD_SAVE_BOOKING: { actions: "toggleShouldSaveBooking" },
      INITIALIZE_CONTEXT: { actions: "initializeForm" },
      REINITIALIZE_CHECKOUT: { actions: "updateCheckout" },
      GO_TO_STEP: values(NEW_ORDER_FORM_STEPS).map((state) => ({
        target: `.${state}`,
        guard: ({ event }) => event.step === state,
      })),
    },
  },
  {
    actions: {
      startLoading: assign(() => ({ loading: true })),
      endLoading: assign(() => ({ loading: false })),
      updateOrderAndPrice: assign(({ event, context }) => {
        const { order, price } = event.output;
        const { repeat_settings } = order;

        let refs = context.references;

        if (context.customer.pricing.paymentType === CHECKOUT_PAYMENT_TYPE) {
          refs = [{ reference: uniqueReferenceNumber(), date: order.startDate }];
          if (repeat_settings.type === REPEAT_ORDER_TYPES.EXACT_DATES) {
            refs = [
              ...refs,
              ...repeat_settings.dates.map((date) => {
                const repeatedDate = refs.find((reference) => reference.date === date);
                return repeatedDate ?? { reference: uniqueReferenceNumber(), date };
              }),
            ];
          }
        }

        return { order, price, checkout: { ...initialContext.checkout, id: context.checkout.id }, references: refs };
      }),
      updateCheckoutError: assign(({ event, context }) => ({
        checkout: { ...context.checkout, error: event.error?.message ?? initialContext.checkout.error },
      })),
      updateCheckout: assign(({ event, context }) => ({
        checkout: { ...context.checkout, ...(event.output ?? event.values) },
      })),
      updateReferences: assign(({ event }) => (event.output ? { references: event.output.references } : {})),
      initializeForm: assign(({ event, context }) => ({
        order: event.values.order ?? context.order,
        customer: event.values.customer ?? context.customer,
        references: event.values.references ?? context.references,
      })),
      resetForm: assign(({ context }) => ({ ...initialContext, customer: context.customer })),
      toggleShouldSaveBooking: assign(({ event }) => ({ save: event.values })),
      toggleFormDirty: assign(({ event }) => ({ dirty: event.values })),
    },
    guards: {
      isNewOrderChanged: ({ context, event }) => !isEqual(context.order, event.values),
      isAlreadyCreated: ({ context }) => !!first(context.references)?.id,
      isImmediatePayment: ({ context }) => context.customer.pricing.paymentType === CHECKOUT_PAYMENT_TYPE,
      isStripeInitialized: ({ context }) => !!context.checkout.secret,
      shouldSaveBooking: ({ context }) => context.save,
    },
    actors: {
      calculateOrderPriceActor,
      preCheckoutActor,
      submitBookingActor,
      checkoutActor,
      saveBookingActor,
    },
  }
);
